import React, { useEffect, useState } from 'react';
import styles from './CoachInfo.module.css';

// Format any digit-bearing input as (xxx) xxx-xxxx, truncated to 10 digits.
// Idempotent — re-formatting an already-formatted value returns the same string.
function formatPhone(input) {
  if (!input) return '';
  const d = String(input).replace(/\D/g, '').slice(0, 10);
  if (d.length === 0) return '';
  if (d.length <= 3) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

const FIELDS = [
  { key: 'coachName',    label: 'Coach Name',    placeholder: 'Michael Lopez' },
  { key: 'title',        label: 'Title / Role',  placeholder: 'Owner & Head Coach' },
  { key: 'businessName', label: 'Business Name', placeholder: 'Next Gen Sports Co.' },
  { key: 'phone',        label: 'Phone',         placeholder: '(720) 555-1234' },
  { key: 'email',        label: 'Email',         placeholder: 'nextgensportsco@gmail.com' },
  { key: 'location',     label: 'Location',      placeholder: 'Parker, CO' },
  { key: 'website',      label: 'Website (optional)',   placeholder: 'nextgensportsco.com' },
  { key: 'instagram',    label: 'Instagram (optional)', placeholder: '@nextgensportsco' },
];

export default function CoachInfo({ API, authHeader, showToast }) {
  const [data, setData]       = useState(null);
  const [original, setOrig]   = useState(null);
  const [saving, setSaving]   = useState(false);
  const [editing, setEditing] = useState(false);
  const [error, setError]     = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/coach-contact`);
        const json = await res.json();
        setData(json || {});
        setOrig(JSON.stringify(json || {}));
      } catch (e) { setError(e.message); }
    })();
  }, [API]);

  if (error) return <div className={styles.page}><p className={styles.muted}>Failed to load: {error}</p></div>;
  if (!data) return <div className={styles.page}><p className={styles.muted}>Loading…</p></div>;

  const dirty = JSON.stringify(data) !== original;

  const set = (key, value) => {
    // Phone is the only field with input formatting today; everything else
    // passes through unchanged so we can add more masks later if needed.
    const v = key === 'phone' ? formatPhone(value) : value;
    setData(d => ({ ...d, [key]: v }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/coach-contact`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(data),
      });
      const json = await res.json();
      if (!res.ok) { showToast(json.error || 'Save failed', 'error'); return; }
      setData(json);
      setOrig(JSON.stringify(json));
      setEditing(false);
      showToast('Coach info saved');
    } catch { showToast('Network error', 'error'); }
    finally { setSaving(false); }
  };

  const cancel = () => {
    setData(JSON.parse(original));
    setEditing(false);
  };

  return (
    <div className={styles.page}>
      <div className={styles.topBar}>
        <div>
          <h1 className={styles.pageTitle}>Coach Info</h1>
          <p className={styles.pageSubtitle}>
            Shown on the parent login under "Need an account?" and on every parent's dashboard once logged in — they see this card and can text, email, or call you directly.
          </p>
        </div>
      </div>

      <div className={styles.card}>
        {FIELDS.map(f => {
          const isPhone = f.key === 'phone';
          // The phone is always rendered formatted, even when read-only — so the
          // raw value stored in the DB (which may or may not already be masked)
          // shows up consistently as (xxx) xxx-xxxx everywhere.
          const displayValue = isPhone ? formatPhone(data[f.key]) : (data[f.key] || '');
          return (
            <div key={f.key} className={styles.field}>
              <label className={styles.fieldLabel}>{f.label}</label>
              <input
                className={styles.fieldInput}
                type="text"
                value={displayValue}
                onChange={e => set(f.key, e.target.value)}
                placeholder={f.placeholder}
                disabled={!editing || saving}
                inputMode={isPhone ? 'tel' : undefined}
              />
            </div>
          );
        })}
        <div className={styles.field}>
          <label className={styles.fieldLabel}>Message to Parents</label>
          <textarea
            className={`${styles.fieldInput} ${styles.textarea}`}
            rows={3}
            value={data.message || ''}
            onChange={e => set('message', e.target.value)}
            placeholder="Parent Portal accounts are set up by your coach. Reach out and we'll get you onboarded."
            disabled={!editing || saving}
          />
        </div>

        <div className={styles.actions}>
          {editing ? (
            <>
              <span className={styles.dirtyHint}>{dirty ? 'Unsaved changes' : 'No changes'}</span>
              <button className={styles.cancelBtn} onClick={cancel} disabled={saving}>Cancel</button>
              <button className={styles.saveBtn} onClick={save} disabled={saving || !dirty}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </>
          ) : (
            <button className={styles.saveBtn} onClick={() => setEditing(true)}>
              ✎ Edit
            </button>
          )}
        </div>
      </div>

      {/* Live preview of what parents see */}
      <div className={styles.previewWrap}>
        <div className={styles.previewLabel}>Parent-side preview</div>
        <div className={styles.previewCard}>
          <div className={styles.previewName}>{data.coachName || 'Coach Name'}</div>
          {data.title && <div className={styles.previewTitle}>{data.title}</div>}
          {data.businessName && <div className={styles.previewBiz}>{data.businessName}</div>}
          {data.message && <p className={styles.previewMsg}>{data.message}</p>}
          <div className={styles.previewLinks}>
            {data.phone    && <a href={`tel:${data.phone.replace(/\D/g,'')}`}>📞 {formatPhone(data.phone)}</a>}
            {data.email    && <a href={`mailto:${data.email}`}>✉ {data.email}</a>}
            {data.location && <span>📍 {data.location}</span>}
            {data.website  && <a href={data.website.startsWith('http') ? data.website : `https://${data.website}`} target="_blank" rel="noopener noreferrer">🌐 {data.website}</a>}
            {data.instagram && <a href={`https://instagram.com/${data.instagram.replace(/^@/, '')}`} target="_blank" rel="noopener noreferrer">◎ {data.instagram}</a>}
          </div>
        </div>
      </div>
    </div>
  );
}
